import { action, computed, observable, makeObservable } from 'mobx';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { WithdrawalsListState } from 'src/domains/players/state/WithdrawalsListState';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { PaymentMethodDropdownState } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/PaymentMethodDropdown.state';
import { UsersState } from 'src/domains/players/state/UsersState';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { Amount } from 'src_common/common/amount/Amount';
import {
    validateAmountRequire,
    validateAmountPattern,
    validateMinAmountNew,
} from 'src/domains/players/webview/components/ValidatorsNew';
import { WithdrawWorldpaySteps, WithdrawWorldpayStepsType } from './WithdrawWorldpayJourney';
import { TrpcClient } from 'src/appState/TrpcClient';
import { amountViewToValue } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/helpers/amountViewToValue';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { Common } from 'src/domains/common/Common';

export class WithdrawWorldpayFormState {
    @observable public withdrawErrorMessage: string | null = null;
    @observable public isWithdrawFormSubmitting = false;

    public stepsState: WithdrawWorldpaySteps;
    public amountState: FormInputState<string, Amount>;

    public constructor(
        private readonly trpcClient: TrpcClient,
        private readonly usersState: UsersState,
        private readonly language: LanguagesState,
        private readonly withdrawalsListState: WithdrawalsListState,
        private readonly googleTagManager: GoogleTagManagerState,
        private readonly paymentMethodDropdownState: PaymentMethodDropdownState,
        private readonly minWithdrawAmount: Amount,
        private readonly basicDataModel: BasicDataModel,
        private readonly common: Common
    ) {
        makeObservable(this);
        this.stepsState = new WithdrawWorldpaySteps();
        this.amountState = FormInputState.new('')
            .map(validateAmountRequire)
            .map(validateAmountPattern)
            .map(
                validateMinAmountNew(
                    this.minWithdrawAmount,
                    this.language.getTranslation(
                        'errors.min-withdraw-new',
                        'Minimum withdraw amount is {currencySymbol}{minValue}',
                        { currencySymbol: this.usersState.moneySymbol, minValue: this.minWithdrawAmount.value }
                    )
                )
            );
    }

    @computed public get showBalance(): string | null {
        const withdrawableBalance = this.usersState.walletData.valueReady?.withdrawableBalance;
        if (withdrawableBalance !== undefined) {
            return this.usersState.money(new Amount(withdrawableBalance));
        }
        return null;
    }

    public handleAmountInputChange = (event: React.SyntheticEvent<HTMLInputElement>): void => {
        const value = event.currentTarget.value;
        const formatValue = amountViewToValue(value);
        this.amountState.setValue(formatValue);
    };

    public setAmount = (): void => {
        const amount = parseFloat(this.amountState.value);
        if (isNaN(amount) === true || amount === 0) {
            return this.amountState.setValue('');
        }
        return this.amountState.setValue(amount.toFixed(2));
    };

    public handlePaymentAdditionsChange = (amount: Amount): void => {
        this.amountState.setValue(amount.value);
        this.amountState.setAsVisited();
    };

    @action public submitWithdrawForm = async (): Promise<void> => {
        this.amountState.setAsVisited();
        this.isWithdrawFormSubmitting = true;
        this.withdrawErrorMessage = null;

        if (
            this.paymentMethodDropdownState.selectedPaymentMethod === undefined ||
            this.amountState.result.value.type === 'error'
        ) {
            this.isWithdrawFormSubmitting = false;
            return;
        }

        try {
            const currency = this.basicDataModel.currency ?? 'GBP';
            const amount = parseFloat(this.amountState.result.value.data.value);

            const withdrawalResponse = await this.trpcClient.client.worldpayRouter.withdrawal.mutate({
                amount,
                currencyCode: currency,
                paymentMethodType: 'Card',
                tokenHref: this.paymentMethodDropdownState.selectedPaymentMethod.providerReference ?? undefined,
                description: 'Withdrawal',
            });

            if (withdrawalResponse.responseStatus === 'error') {
                this.isWithdrawFormSubmitting = false;
                this.stepsState.redirectToFailureView('serverIssue');
                return;
            }

            this.googleTagManager.addWithdrawnTag(this.amountState.result.value.data.value);
            await this.withdrawalsListState.refresh();
            this.amountState.reset();
            this.isWithdrawFormSubmitting = false;
            this.stepsState.redirectToPending();
        } catch {
            this.isWithdrawFormSubmitting = false;
            this.stepsState.redirectToFailureView('serverIssue');
        }
    };

    @computed public get currentStep(): WithdrawWorldpayStepsType {
        const methods =
            this.common.features.withdrawDropdownNew === true
                ? this.paymentMethodDropdownState.savedPaymentMethodsWithNetDeposits
                : this.paymentMethodDropdownState.savedPaymentMethods;

        let status;
        if (this.common.features.withdrawDropdownNew === true) {
            if (this.common.session.userId === null) {
                status = 'error';
            } else {
                const savedCards = this.paymentMethodDropdownState.savedCardsWithNetDepositsResponseWrapper.get([
                    this.common.session.userId,
                ]);
                if (savedCards.data === null) {
                    status = 'loading';
                } else if (savedCards.data.responseStatus === 'error') {
                    status = 'error';
                } else {
                    status = 'ready';
                }
            }
        } else {
            status = this.paymentMethodDropdownState.savedPaymentMethodResource.get().type;
        }

        const isCardNotSaved = (methods === null || methods.length === 0) && status === 'ready';

        if (isCardNotSaved) {
            return {
                type: 'failure-view',
                failureType: 'noPaymentMethods',
            };
        }

        return this.stepsState.step;
    }

    @computed public get isBtnDisabled(): boolean {
        const withdrawableBalance = this.usersState.walletData.valueReady?.withdrawableBalance;
        if (withdrawableBalance === undefined) {
            return true;
        }
        if (this.amountState.result.value.type === 'error') {
            return true;
        }
        const withdrawableBalanceAmount = new Amount(withdrawableBalance);
        if (this.amountState.result.value.data.isGreaterThan(withdrawableBalanceAmount)) {
            return true;
        }
        if (this.paymentMethodDropdownState.selectedPaymentMethod === undefined) {
            return true;
        }
        return false;
    }
}
